import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { PageHelper } from 'Components/PageHelper';
import { deleteCookie, getCookies } from 'services/cookies-service';
import { useAuthTenant } from 'store/auth';
import { Main as Container } from 'Components/Main';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';

import { Dialog, PageHeader, Button, EditSvg } from 'Components/ui';

const INK = 'var(--vo-ink)';
const BLUE = 'var(--color-brand)';
const MUTED = 'var(--vo-slate)';
const GREEN = 'var(--vo-success)';
const AMBER = 'var(--vo-warning)';
const RED = 'var(--vo-danger)';
const CARD_SHADOW = '0 1px 3px var(--vo-escuro-8)';

const STATUS_CONFIG = {
  installed: { color: GREEN, bg: 'var(--vo-success-10)' },
  instalado: { color: GREEN, bg: 'var(--vo-success-10)' },
  pending: { color: AMBER, bg: 'var(--vo-warning-10)' },
  pendente: { color: AMBER, bg: 'var(--vo-warning-10)' },
  not_installed: { color: RED, bg: 'var(--vo-danger-10)' },
  uninstalled: { color: RED, bg: 'var(--vo-danger-10)' },
  nao_instalado: { color: RED, bg: 'var(--vo-danger-10)' },
};

const getStatusConfig = status => {
  if (!status) return null;
  const key = status.toLowerCase().replace(/[\s-]/g, '_');
  return STATUS_CONFIG[key] ?? { color: MUTED, bg: 'var(--vo-slate-10)' };
};

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 30px 0;
`;


const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: ${CARD_SHADOW};
  border-left: 4px solid ${BLUE};
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;

const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
  line-height: 1.55;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: ${CARD_SHADOW};
  display: flex;
  align-items: center;
  gap: 14px;
  border-left: 4px solid ${({ $color }) => $color};
`;

const StatNumber = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1;
`;

const StatLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${MUTED};
  margin: 0;
  line-height: 1.4;
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: ${CARD_SHADOW};
  overflow: hidden;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const StyledThead = styled.thead`
  background: var(--vo-mist);
`;

const StyledTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 16px;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`;

const StyledTr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  transition: background 0.12s;
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;

const StyledTd = styled.td`
  padding: 13px 16px;
`;

const IdText = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
`;

const SiteText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${INK};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const getStatusLabel = (status, t) => {
  if (!status) return '';
  const key = status.toLowerCase().replace(/[\s-]/g, '_');
  const map = {
    installed: t('cookies:page.status.installed'),
    instalado: t('cookies:page.status.installed'),
    pending: t('cookies:page.status.pending'),
    pendente: t('cookies:page.status.pending'),
    not_installed: t('cookies:page.status.notInstalled'),
    uninstalled: t('cookies:page.status.notInstalled'),
    nao_instalado: t('cookies:page.status.notInstalled'),
  };
  return map[key] ?? status;
};

export const Cookies = () => {
  const { t } = useTranslation(['cookies']);
  const selectedTenant = useSelector(useAuthTenant);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const navigate = useNavigate();

  const { isLoading, data: cookie } = useQuery(['getCookies', reFetch], async () => {
    if (selectedTenant.slug === undefined) return null;
    const result = await getCookies(selectedTenant.slug);
    if (result?.status === 200) return result?.data;
    return null;
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const mutationDelete = useMutation(
    id => deleteCookie(id),
    {
      onError: error => {
        const errorMessage = error.response?.data?.message || error.message;
        Dialog.alert({ title: t('cookies:page.swal.errorTitle'), message: errorMessage, icon: 'error' });
      },
      onSuccess: () => {
        onClose();
        setRefetch(!reFetch);
        Dialog.alert({
          title: t('cookies:page.swal.successTitle'),
          message: t('cookies:page.swal.deleteSuccessText'),
          icon: 'success',
        });
      },
    },
  );

  const tourSteps = [
    { target: 'body', content: t('cookies:page.tour.step1'), disableBeacon: true, disableScrolling: true, placement: 'center' },
    { target: '[data-tour="page-title"]', content: t('cookies:page.tour.step2'), disableScrolling: true },
    { target: '[data-tour="add-button"]', content: t('cookies:page.tour.step3'), disableScrolling: true },
    { target: '[data-tour="info-card"]', content: t('cookies:page.tour.step4'), disableScrolling: true },
    { target: '[data-tour="totalizers"]', content: t('cookies:page.tour.step5'), disableScrolling: false },
    { target: '[data-tour="table"]', content: t('cookies:page.tour.step6'), disableScrolling: false },
    { target: 'body', content: t('cookies:page.tour.step7'), disableScrolling: true, placement: 'center' },
  ];

  const hasData = !isLoading && cookie && cookie.disclaimers?.length > 0;
  const isEmpty = !isLoading && (!cookie || cookie.disclaimers?.length === 0);

  return (
    <Container>
      <PageWrap>
        {/* Page header */}
        <PageHeader
          data-tour="page-title"
          title={t('cookies:page.title')}
          segments={[
            { label: t('common:breadcrumbs.compliance') },
            { label: t('cookies:page.breadcrumbLabel') },
          ]}
          actions={
            <Button
              variant="primary"
              data-tour="add-button"
              onClick={() => navigate('/cookies/add')}
              sx={{ height: '44px', fontSize: '14px', padding: '0 20px' }}
              startIcon={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              }
            >
              {t('cookies:page.addButton')}
            </Button>
          }
        />

        {/* Info banner */}
        <InfoBanner data-tour="info-card">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10"/><circle cx="8.5" cy="8.5" r="1.5"/><circle cx="15" cy="9" r="1"/><circle cx="12" cy="15" r="2"/>
          </svg>
          <div>
            <InfoTitle>{t('cookies:page.title')}</InfoTitle>
            <InfoDesc>{t('cookies:page.description')}</InfoDesc>
          </div>
        </InfoBanner>

        {isLoading && <PageLoaderList />}

        {isEmpty && <NoData description={t('cookies:page.empty')} />}

        {hasData && (
          <>
            {/* Stat cards */}
            <StatsGrid data-tour="totalizers">
              <StatCard $color="var(--vo-celeste)">
                <StatNumber $color="var(--vo-celeste)">{cookie.totalizer?.unique_users ?? 0}</StatNumber>
                <StatLabel>{t('cookies:page.totalizers.visitorsLabel1')}<br/>{t('cookies:page.totalizers.visitorsLabel2')}</StatLabel>
              </StatCard>
              <StatCard $color={GREEN}>
                <StatNumber $color={GREEN}>{cookie.totalizer?.opt_ins ?? 0}</StatNumber>
                <StatLabel>{t('cookies:page.totalizers.optInLabel1')}<br/>{t('cookies:page.totalizers.optInLabel2')}</StatLabel>
              </StatCard>
              <StatCard $color={RED}>
                <StatNumber $color={RED}>{cookie.totalizer?.opt_out ?? 0}</StatNumber>
                <StatLabel>{t('cookies:page.totalizers.optOutLabel1')}<br/>{t('cookies:page.totalizers.optOutLabel2')}</StatLabel>
              </StatCard>
            </StatsGrid>

            {/* Table */}
            <TableCard data-tour="table">
              <StyledTable>
                <StyledThead>
                  <tr>
                    <StyledTh>{t('cookies:page.table.id')}</StyledTh>
                    <StyledTh>{t('cookies:page.table.site')}</StyledTh>
                    <StyledTh>{t('cookies:page.table.status')}</StyledTh>
                    <StyledTh $center>{t('cookies:page.table.actions')}</StyledTh>
                  </tr>
                </StyledThead>
                <tbody>
                  {cookie.disclaimers.map(item => {
                    const statusCfg = getStatusConfig(item.status);
                    return (
                      <StyledTr key={item.id}>
                        <StyledTd>
                          <IdText>{item.id}</IdText>
                        </StyledTd>
                        <StyledTd>
                          <SiteText>{item.url}</SiteText>
                        </StyledTd>
                        <StyledTd>
                          {statusCfg && (
                            <StatusBadge $color={statusCfg.color} $bg={statusCfg.bg}>
                              {getStatusLabel(item.status, t)}
                            </StatusBadge>
                          )}
                        </StyledTd>
                        <StyledTd>
                          <ActionsCell>
                            <Button iconOnly variant="icon" title={t('cookies:page.actions.view')} onClick={() => navigate(`/cookies/show/${item.id}`)}>
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                              </svg>
                            </Button>
                            <Button iconOnly variant="icon" title={t('cookies:page.actions.edit')} onClick={() => navigate(`/cookies/edit/${item.id}`)}>
                              <EditSvg />
                            </Button>
                            <Button iconOnly variant="icon" danger title={t('cookies:page.actions.delete')} onClick={() => onDelete(item)}>
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            </Button>
                          </ActionsCell>
                        </StyledTd>
                      </StyledTr>
                    );
                  })}
                </tbody>
              </StyledTable>
            </TableCard>
          </>
        )}
      </PageWrap>

      <Dialog
        open={open}
        onConfirm={() => mutationDelete.mutate(itemToDelete?.id)}
        onCancel={onClose}
        message={t('cookies:page.deleteConfirm', { url: itemToDelete?.url })}
      />

      <PageHelper steps={tourSteps} tourKey="cookies-list" />
    </Container>
  );
};
